import { useQuery } from '@apollo/client';
import { Alert, Button } from '@unmind/design-system';
import { tracking } from 'App/Tracking';
import {
  getDefaultPractitionerDateRange,
  LocationModal,
  LocationModalPage,
  PractitionerAvatar,
  useHasExistingBookingOrRequest,
} from 'Book/shared';
import { practitionerMatchesQuery } from 'Services/BeGateway/Practitioner/__generated__/practitionerMatchesQuery';
import { practitionerQuery } from 'Services/BeGateway/Practitioner/__generated__/practitionerQuery';
import {
  PRACTITIONER_MATCHES_QUERY,
  PRACTITIONER_QUERY,
} from 'Services/BeGateway/Practitioner/practitioner.services';
import { ScreenReaderContent, useAssignFocus } from 'Shared/Accessibility';
import LoadingIndicator from 'Shared/LoadingIndicator';
import SectionError from 'Shared/SectionError';
import { useUserLocation } from 'Account/hooks';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { BEGatewayQueryContext, defaultQueryConfig } from 'utils/apollo';
import { useUserEntitlements } from 'hooks/useUserEntitlements';

import { Drawer, ProfileDetails } from './components';
import { BookingAlertContainer, Content, StickyColumn } from './styles';
import { BookingType } from './types';

interface Props {
  source?: string;
}

export default ({ source }: Readonly<Props>) => {
  const { entitlements, loading: entitlementsLoading } = useUserEntitlements();
  const aiMatchingEnabled =
    entitlements?.talkAiMatching && !entitlementsLoading;
  const { t } = useTranslation('book', { keyPrefix: 'practitioner.book' });
  const nameTextRef = useRef<HTMLHeadingElement>(null);
  const {
    userLocationData,
    hasValidLocationData,
    loading: userLocationDataLoading,
  } = useUserLocation();
  const { practitionerId } = useParams<{ practitionerId: string }>();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const today = useMemo(() => new Date(), []);
  const practitionerQueryVariables = {
    id: practitionerId,
    country: userLocationData?.user?.country?.value,
    state: userLocationData?.user?.state?.value,
    ...getDefaultPractitionerDateRange(today),
  };

  const {
    hasBookingOrRequest: hasBookingOrRequestWithAnotherPractitioner,
    loading: hasBookingOrRequestWithAnotherPractitionerLoading,
  } = useHasExistingBookingOrRequest(practitionerId);

  const {
    data: practitionerData,
    refetch,
    error: practitionerDataError,
    loading: practitionerDataLoading,
  } = useQuery<practitionerQuery>(PRACTITIONER_QUERY, {
    variables: {
      ...practitionerQueryVariables,
    },
    skip: !practitionerId || !hasValidLocationData,
    ...BEGatewayQueryContext,
  });

  const { data: practitionerMatchesData } = useQuery<practitionerMatchesQuery>(
    PRACTITIONER_MATCHES_QUERY,
    {
      skip: !aiMatchingEnabled,
      ...defaultQueryConfig,
      ...BEGatewayQueryContext,
    },
  );

  useAssignFocus(
    nameTextRef,
    Boolean(practitionerData && userLocationData?.user && !isOpen),
  );

  const practitioner = practitionerData?.practitioner;
  const practitionerName = `${practitioner?.firstName} ${practitioner?.lastName}`;
  const practitionerIsMatch = (
    practitionerMatchesData?.practitionerMatches ?? []
  ).some(e => e?.id === practitionerId);
  const isDirectBookingEnabled = Boolean(
    practitioner?.availability?.hasAvailability,
  );
  const bookingType: BookingType = isDirectBookingEnabled
    ? BookingType.DirectBooking
    : BookingType.BookingRequest;

  const handleOpenDrawer = () => {
    if (!isOpen) {
      tracking.track('talk-practitioner-book-appointment-selected', {
        bookingType,
        practitionerId,
      });
    }

    setIsOpen(!isOpen);
  };

  if (practitionerDataError && !practitionerDataLoading && !practitionerData) {
    return (
      <SectionError
        onRetry={() => {
          void refetch({
            ...practitionerQueryVariables,
          });
        }}
      />
    );
  }

  if (
    userLocationDataLoading ||
    practitionerDataLoading ||
    hasBookingOrRequestWithAnotherPractitionerLoading
  ) {
    return <LoadingIndicator />;
  }

  return (
    <>
      {practitionerData?.practitioner && userLocationData?.user ? (
        <>
          <ScreenReaderContent as="h1" ref={nameTextRef} tabIndex={-1}>
            {practitionerName}
          </ScreenReaderContent>

          {hasBookingOrRequestWithAnotherPractitioner ? (
            <BookingAlertContainer>
              <Alert
                variant="info"
                size="medium"
                title={t(`bookingDisabled.${bookingType}.title`)}
                body={t(`bookingDisabled.text`)}
              />
            </BookingAlertContainer>
          ) : null}

          <Content>
            <StickyColumn>
              <PractitionerAvatar
                practitioner={practitionerData?.practitioner}
              />
              <Button
                variant="primary"
                onClick={handleOpenDrawer}
                label={t(`ctas.${isDirectBookingEnabled ? 'book' : 'request'}`)}
                disabled={hasBookingOrRequestWithAnotherPractitioner}
                data-cy="open-booking-panel-button"
              />
            </StickyColumn>
            <ProfileDetails practitioner={practitionerData?.practitioner} />
            <Drawer
              practitioner={practitionerData?.practitioner}
              hasBookingOrRequestWithAnotherPractitioner={
                hasBookingOrRequestWithAnotherPractitioner
              }
              userCountryState={userLocationData.user}
              source={source}
              practitionerIsMatch={practitionerIsMatch}
              isOpen={isOpen}
              setIsOpen={setIsOpen}
            />
          </Content>
        </>
      ) : null}
      <LocationModal page={LocationModalPage.Practitioner} />
    </>
  );
};
